<div class="create-payment-receipt-container">

    <!-- Date range card -->
    <mat-card class="content-card">
        <mat-card-content>

            <div class="card-header">
                <h2 class="page-title">Create Payment Receipt</h2>
            </div>

            <p class="page-subtitle">
                Pick a payment date range to review which receipts were sent, then select the
                missing ones and generate them. Nothing is inserted until you click Generate.
            </p>

            <form [formGroup]="rangeForm" (ngSubmit)="onSubmit()">
                <div class="filter-grid">

                    <mat-form-field appearance="outline">
                        <mat-label>From (date paid)</mat-label>
                        <input matInput [matDatepicker]="fromPicker" formControlName="from"
                               placeholder="Select date">
                        <mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
                        <mat-datepicker #fromPicker></mat-datepicker>
                    </mat-form-field>

                    <mat-form-field appearance="outline">
                        <mat-label>To (date paid)</mat-label>
                        <input matInput [matDatepicker]="toPicker" formControlName="to"
                               placeholder="Select date">
                        <mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
                        <mat-datepicker #toPicker></mat-datepicker>
                    </mat-form-field>

                </div>

                <div class="filter-actions">
                    <button mat-stroked-button type="button" (click)="onClear()">
                        <mat-icon>clear</mat-icon>
                        Clear
                    </button>
                    <button mat-raised-button color="primary" type="submit" [disabled]="loading">
                        <mat-icon>search</mat-icon>
                        Submit
                    </button>
                </div>
            </form>

        </mat-card-content>
    </mat-card>

    <!-- Results card -->
    <mat-card class="content-card" *ngIf="loaded">
        <mat-card-content>

            <div class="card-header">
                <h3 class="section-title">Payment Receipts</h3>
                <div class="header-actions">
                    <span class="count-chip missing">MISSING: {{ counts.MISSING }}</span>
                    <span class="count-chip queued">QUEUED: {{ counts.QUEUED }}</span>
                    <span class="count-chip no-email">NO_EMAIL: {{ counts.NO_EMAIL }}</span>
                    <button mat-raised-button color="primary" type="button"
                            [disabled]="selected.size === 0 || generating"
                            (click)="onGenerate()">
                        <mat-spinner *ngIf="generating" diameter="18" class="btn-spinner"></mat-spinner>
                        <mat-icon *ngIf="!generating">mark_email_read</mat-icon>
                        {{ generating ? 'Generating…' : 'Generate Selected (' + selected.size + ')' }}
                    </button>
                </div>
            </div>

            <div class="loading-row" *ngIf="loading">
                <mat-spinner diameter="32"></mat-spinner>
            </div>

            <p class="empty-state" *ngIf="!loading && dataSource.data.length === 0">
                No eligible payments found for the selected date range.
            </p>

            <div class="table-wrapper" *ngIf="!loading && dataSource.data.length > 0">
                <table mat-table [dataSource]="dataSource" class="flat-table">

                    <!-- Select -->
                    <ng-container matColumnDef="select">
                        <th mat-header-cell *matHeaderCellDef>
                            <mat-checkbox [checked]="isAllMissingSelected()"
                                          [indeterminate]="isSomeMissingSelected()"
                                          [disabled]="missingCount === 0"
                                          (change)="toggleAllMissing()"
                                          matTooltip="Select all MISSING">
                            </mat-checkbox>
                        </th>
                        <td mat-cell *matCellDef="let row">
                            <mat-checkbox [checked]="selected.has(row.paymentHistoryRowId)"
                                          [disabled]="!isSelectable(row)"
                                          (change)="toggleRow(row)"
                                          (click)="$event.stopPropagation()">
                            </mat-checkbox>
                        </td>
                    </ng-container>

                    <!-- Status -->
                    <ng-container matColumnDef="status">
                        <th mat-header-cell *matHeaderCellDef>Status</th>
                        <td mat-cell *matCellDef="let row">
                            <span class="status-badge"
                                  [class.missing]="row.status === 'MISSING'"
                                  [class.queued]="row.status === 'QUEUED'"
                                  [class.no-email]="row.status === 'NO_EMAIL'">
                                {{ row.status }}
                            </span>
                        </td>
                    </ng-container>

                    <!-- Loan Number -->
                    <ng-container matColumnDef="loanNumber">
                        <th mat-header-cell *matHeaderCellDef>Loan #</th>
                        <td mat-cell *matCellDef="let row">{{ row.loanNumber }}</td>
                    </ng-container>

                    <!-- Customer Name -->
                    <ng-container matColumnDef="customerName">
                        <th mat-header-cell *matHeaderCellDef>Customer</th>
                        <td mat-cell *matCellDef="let row">{{ row.customerName }}</td>
                    </ng-container>

                    <!-- Recipient Email -->
                    <ng-container matColumnDef="recipientEmail">
                        <th mat-header-cell *matHeaderCellDef>Email</th>
                        <td mat-cell *matCellDef="let row">{{ row.recipientEmail || '—' }}</td>
                    </ng-container>

                    <!-- Date Paid -->
                    <ng-container matColumnDef="datePaid">
                        <th mat-header-cell *matHeaderCellDef>Date Paid</th>
                        <td mat-cell *matCellDef="let row">
                            {{ FormatUtil.formatDate(row.datePaid, 'YYYY-MM-DD') }}
                        </td>
                    </ng-container>

                    <!-- Payment Reference -->
                    <ng-container matColumnDef="paymentReferenceNo">
                        <th mat-header-cell *matHeaderCellDef>Payment Ref</th>
                        <td mat-cell *matCellDef="let row">{{ row.paymentReferenceNo }}</td>
                    </ng-container>

                    <!-- Payment Amount -->
                    <ng-container matColumnDef="paymentAmount">
                        <th mat-header-cell *matHeaderCellDef>Amount</th>
                        <td mat-cell *matCellDef="let row">
                            {{ FormatUtil.formatCurrency(+row.paymentAmount) }}
                        </td>
                    </ng-container>

                    <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
                    <tr mat-row *matRowDef="let row; columns: displayedColumns"
                        class="status-row"
                        [class.selectable]="isSelectable(row)"
                        [class.selected-row]="selected.has(row.paymentHistoryRowId)"
                        (click)="toggleRow(row)"></tr>
                </table>

                <mat-paginator [pageSize]="25" [pageSizeOptions]="[25, 50, 100]"
                               showFirstLastButtons></mat-paginator>
            </div>

        </mat-card-content>
    </mat-card>

</div>